// src/DoctorProfileExpressGMaps.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { GoogleMap, Marker, useLoadScript, Autocomplete } from "@react-google-maps/api";
import { Activity, Navigation, Save, Settings, X, Plus, CheckCircle2, Shield, Info } from "lucide-react";

/* -------------------------------
   Config
----------------------------------*/
const DEFAULT_CENTER = { lat: 41.3275, lng: 19.8187 };
const MAP_LIBRARIES = ["places"];
const CURRENCIES = ["EUR", "ALL"];
const SPECIALTIES = [
  "Mjekësi e përgjithshme","Mjekësi familjare","Mjekësi interne","Kardiologji","Kardiokirurgji","Pneumologji","Gastroenterologji","Hepatologji","Endokrinologji","Diabetologji","Nefrologji","Hematologji","Reumatologji","Sëmundje infektive","Alergologji dhe Imunologji","Neurologji","Neurokirurgji","Dermatologji","Oftalmologji","ORL (Otorinolaringologji)","Gjinekologji & Obstetrikë","Urologji","Onkologji","Radiologji","Radiologji ndërhyrëse","Anesteziologji dhe Reanimacion","Patologji (Anatomo-patologji)","Mjekësi e urgjencës","Mjekësi e punës","Gjenetikë Mjekësore","Mjekësi sportive","Mjekësi fizike dhe Rehabilitim","Ortopedi","Kirurgji e përgjithshme","Kirurgji plastike & rikonstruktive","Kirurgji Maksilofaciale","Kirurgji torakale","Kirurgji vaskulare","Kirurgji pediatrike","Pediatri","Neonatologji","Geriatri","Psikologji","Psikiatri","Psikiatri e fëmijëve & adoleshentëve","Logopedi","Dietologji / Nutricionizëm","Mjekësi laboratorike","Mikrobiologji","Infermier","Dentist","Fizioterapist"
];

const formatMoney = (value, currency = "EUR") => {
  if (typeof value !== "number" || isNaN(value)) return "—";
  return new Intl.NumberFormat("sq-AL", { style: "currency", currency }).format(value);
};
const normalizePriceToNumber = (text) => {
  if (!text) return null;
  const cleaned = String(text).replace(/[^\d,.\-]/g, "").replace(",", ".");
  const num = Number(cleaned);
  return isNaN(num) ? null : num;
};
const parseDurationToMinutes = (str) => {
  if (!str) return null;
  const s = String(str).toLowerCase().trim();
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  const h = s.match(/(\d+)\s*h/);
  const m = s.match(/(\d+)\s*m/);
  const hours = h ? parseInt(h[1], 10) : 0;
  const mins  = m ? parseInt(m[1], 10) : 0;
  const total = hours * 60 + mins;
  return total > 0 ? total : null;
};
const prettyDuration = (minutes) => {
  if (!minutes || isNaN(minutes)) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const SERVICE_TEMPLATES = {
  "Mjekësi e përgjithshme": [
    { title: "Konsultë e parë", price: 2000, durationMinutes: 20 },
    { title: "Kontroll pasues", price: 1500, durationMinutes: 15 },
  ],
  "Kardiologji": [
    { title: "Konsultë kardiologjike", price: 4000, durationMinutes: 25 },
    { title: "EKG", price: 1500, durationMinutes: 10 },
  ],
  "Dermatologji": [
    { title: "Konsultë dermatologjike", price: 3000, durationMinutes: 20 },
    { title: "Kontroll i lëkurës", price: 2500, durationMinutes: 15 },
  ],
};

/* CLEAR, HUMAN-FRIENDLY PRESETS */
const PRESETS = [
  { key: "mf_9_17",     label: "E hënë–E premte · 09:00–17:00", value: { monFri: ["09:00","17:00"], sat: null,              sun: null } },
  { key: "mf_9_14_sat", label: "E hënë–E shtunë · 09:00–14:00", value: { monFri: ["09:00","14:00"], sat: ["09:00","14:00"], sun: null } },
  { key: "mf_14_20",    label: "Pasdite · 14:00–20:00",         value: { monFri: ["14:00","20:00"], sat: null,              sun: null } },
];

const emptyWorkingHours = () => ({
  monday:    { enabled: true,  start: "09:00", end: "17:00" },
  tuesday:   { enabled: true,  start: "09:00", end: "17:00" },
  wednesday: { enabled: true,  start: "09:00", end: "17:00" },
  thursday:  { enabled: true,  start: "09:00", end: "17:00" },
  friday:    { enabled: true,  start: "09:00", end: "17:00" },
  saturday:  { enabled: false, start: "09:00", end: "14:00" },
  sunday:    { enabled: false, start: "09:00", end: "14:00" },
});

const DAY_LABELS = [
  { key: "monday", label: "E hënë" },
  { key: "tuesday", label: "E martë" },
  { key: "wednesday", label: "E mërkurë" },
  { key: "thursday", label: "E enjte" },
  { key: "friday", label: "E premte" },
  { key: "saturday", label: "E shtunë" },
  { key: "sunday", label: "E diel" },
];

/* -------------------------------
   Toasts
----------------------------------*/
function useToasts() {
  const [list, setList] = useState([]);
  const show = (message) => {
    const id = Math.random().toString(36).slice(2);
    setList((prev) => [...prev, { id, message }]);
    setTimeout(() => setList((prev) => prev.filter(t => t.id !== id)), 2200);
  };
  return { toasts: list, show };
}

/* -------------------------------
   Centered Modal (no deps)
----------------------------------*/
function Modal({ open, title, children, onClose, onContinue }) {
  // lock background scroll
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal">
        <div className="modal-head">
          <div className="modal-title"><Info size={16}/>{title}</div>
          <button className="iconbtn" onClick={onClose} aria-label="Mbyll"><X size={16}/></button>
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-actions">
          <button className="btn ghost" onClick={onClose}>Mbyll</button>
          <button className="btn primary" onClick={onContinue}>Vazhdo</button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------
   Tiny progress ring
----------------------------------*/
function ProgressRing({ value = 0, size = 56, stroke = 6 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <div className="ring">
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r} stroke="#e6eefc" strokeWidth={stroke} fill="none"/>
        <circle cx={size/2} cy={size/2} r={r} stroke="#0a84ff" strokeWidth={stroke} fill="none" strokeLinecap="round"
          style={{ strokeDasharray: c, strokeDashoffset: offset, transition: "stroke-dashoffset .4s ease" }}/>
      </svg>
      <div className="ring-center">{Math.round(value)}%</div>
    </div>
  );
}

/* -------------------------------
   Component
----------------------------------*/
export default function DoctorProfileExpressGMaps() {
  const navigate = useNavigate();
  const { toasts, show } = useToasts();

  // Google Maps
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  const [user, setUser] = useState(null);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  // fields
  const [name, setName] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [address, setAddress] = useState("");
  const [coords, setCoords] = useState(DEFAULT_CENTER);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [zoom, setZoom] = useState(14);
  const [currency, setCurrency] = useState("EUR");
  const [services, setServices] = useState([]);
  const [customService, setCustomService] = useState({ title: "", price: "", duration: "" });
  const [workingHours, setWorkingHours] = useState(emptyWorkingHours());
  const [activePreset, setActivePreset] = useState("");

  // refs
  const mapRef = useRef(null);
  const geocoderRef = useRef(null);
  const autocompleteRef = useRef(null);
  const addressInputRef = useRef(null);

  // info modal state
  const [info, setInfo] = useState({ open: false, title: "", body: null });

  /* Keyboard Save + modal Esc */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (canSave) save();
      }
      if (e.key === "Escape" && info.open) setInfo(s => ({ ...s, open: false }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  /* Auth + hydrate */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { navigate("/login"); return; }
      setUser(u);
      await hydrate(u.uid);
      setBusy(false);
    });
    return () => unsub();
  }, [navigate]);

  /* Prefill service chips */
  useEffect(() => {
    if (!specialty) return;
    if (services.length > 0) return;
    if (SERVICE_TEMPLATES[specialty]) setServices(SERVICE_TEMPLATES[specialty]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialty]);

  /* Geocoder init */
  useEffect(() => {
    if (isLoaded && window.google && !geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  const hydrate = async (uid) => {
    try {
      const snap = await getDoc(doc(db, "doctors", uid));
      if (snap.exists()) {
        const d = snap.data();
        setName(d.name || "");
        setSpecialty(d.specialty || "");
        const loc = d.location || {};
        const c = loc.coordinates || DEFAULT_CENTER;
        setAddress(loc.address || "");
        setCoords(c);
        setMapCenter(c);
        setServices(Array.isArray(d.services) ? d.services : []);
        setWorkingHours(d.workingHours || emptyWorkingHours());
        setCurrency(d.currency || "EUR");
      }
    } catch {
      // ignore
    }
  };

  const minimalValid = () => name.trim().length > 0 && specialty && address.trim().length > 0;

  const invalidDays = useMemo(() => {
    const bad = [];
    for (const { key } of DAY_LABELS) {
      const d = workingHours[key];
      if (!d?.enabled) continue;
      if (!d.start || !d.end) { bad.push(key); continue; }
      const [sh, sm] = d.start.split(":").map(Number);
      const [eh, em] = d.end.split(":").map(Number);
      if (eh*60+em <= sh*60+sm) bad.push(key);
    }
    return bad;
  }, [workingHours]);

  const canSave = minimalValid() && invalidDays.length === 0;

  /* Completeness for actionbar */
  const completeness = useMemo(() => {
    let score = 0;
    if (name.trim()) score += 35;
    if (specialty) score += 25;
    if (address.trim()) score += 25;
    if (services.length > 0) score += 15;
    return Math.min(100, score);
  }, [name, specialty, address, services.length]);

  /* Presets, hours helpers */
  const applyPreset = (value, presetKey) => {
    const w = emptyWorkingHours();
    if (value.monFri) {
      ["monday","tuesday","wednesday","thursday","friday"].forEach(d => {
        w[d] = { enabled: true, start: value.monFri[0], end: value.monFri[1] };
      });
    }
    if (value.sat) w.saturday = { enabled: true, start: value.sat[0], end: value.sat[1] };
    if (value.sun) w.sunday   = { enabled: true, start: value.sun[0], end: value.sun[1] };
    setWorkingHours(w);
    setActivePreset(presetKey);
    setOk("Orari u vendos.");
    show("Orari tipik u përditësua");
  };
  const setDayEnabled = (key, enabled) => setWorkingHours(prev => ({ ...prev, [key]: { ...prev[key], enabled } }));
  const setDayTime = (key, field, value) => setWorkingHours(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

  const openToday = useMemo(() => {
    const jsDay = new Date().getDay();
    const map = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
    const key = map[jsDay];
    const d = workingHours[key];
    if (!d?.enabled) return "Mbyllur sot";
    return `Disponueshëm sot: ${d.start}–${d.end}`;
  }, [workingHours]);

  /* Places handlers */
  const handlePlaceChanged = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place || !place.geometry) {
      setError("Zgjidhni një adresë nga sugjerimet.");
      return;
    }
    const newCoords = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
    setCoords(newCoords);
    setMapCenter(newCoords);
    setZoom(16);
    setAddress(place.formatted_address || place.name || addressInputRef.current?.value || "");
    setOk("Adresa u vendos.");
    show("Adresa u vendos");
  };

  const reverseGeocode = (latLng) => {
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ location: latLng }, (results, status) => {
      if (status === "OK" && results && results[0]) setAddress(results[0].formatted_address);
      else setAddress(`${latLng.lat.toFixed(6)}, ${latLng.lng.toFixed(6)}`);
    });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { setError("Shfletuesi nuk suporton lokacionin."); return; }
    setError(""); setOk("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCoords(c); setMapCenter(c); setZoom(16); reverseGeocode(c);
        setOk("Lokacioni u vendos.");
        show("Lokacioni u vendos");
      },
      () => setError("S’arriti të marrë lokacionin. Zgjidhni nga sugjerimet e adresave."),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  /* Services */
  const addChipFromTemplate = (s) => {
    setServices(prev => prev.some(p => p.title === s.title) ? prev : [...prev, s]);
    show(`Shtuar: ${s.title}`);
  };
  const removeService = (idx) => {
    const t = services[idx]?.title || "Shërbim";
    setServices(prev => prev.filter((_, i) => i !== idx));
    show(`U hoq: ${t}`);
  };
  const addCustomService = () => {
    const title = (customService.title || "").trim();
    if (!title) return;
    const price = normalizePriceToNumber(customService.price);
    const durationMinutes = parseDurationToMinutes(customService.duration);
    setServices(prev => [...prev, { title, price: price ?? null, durationMinutes }]);
    setCustomService({ title: "", price: "", duration: "" });
    show(`Shtuar: ${title}`);
  };

  /* Save */
  // replace your current `save` with this
const save = async () => {
  if (!user) return;
  if (!canSave) {
    setError(invalidDays.length ? "Kontrolloni oraret me të kuqe." : "Plotësoni: Emri, Specialiteti, Adresa.");
    return;
  }

  setSaving(true); setError(""); setOk("");

  // write both forms so any older/newer checks pass
  const completionFields = {
    profileComplete: true,
    profileStatus: "complete",
    profileCompletionPercent: 100,
  };

  const payload = {
    name: name.trim(),
    specialty,
    location: { address: address.trim(), coordinates: coords, placeId: "" },
    workingHours,
    services,
    currency,
    isVerified: false,
    uid: user.uid,
    email: user.email,
    ...completionFields,
  };

  try {
    const dref = doc(db, "doctors", user.uid);
    const uref = doc(db, "users", user.uid);

    // doctors/{uid}
    const dexists = await getDoc(dref);
    if (!dexists.exists()) {
      await setDoc(
        dref,
        { ...payload, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
        { merge: true }
      );
    } else {
      await setDoc(dref, payload, { merge: true });
      await updateDoc(dref, { updatedAt: serverTimestamp() });
    }

    // users/{uid}
    const uexists = await getDoc(uref);
    const userDocBase = {
      name: name.trim(),
      email: user.email,
      role: "doctor",
      ...completionFields,
    };

    if (!uexists.exists()) {
      await setDoc(
        uref,
        { ...userDocBase, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
        { merge: true }
      );
    } else {
      await setDoc(uref, userDocBase, { merge: true });
      await updateDoc(uref, { updatedAt: serverTimestamp() });
    }

    setOk("Profili u publikua!");
    show("Profili u publikua");
    setTimeout(() => navigate("/verify-doctor"), 900);
  } catch (e) {
    setError("Nuk u ruajt profili. Provoni sërish.");
  } finally {
    setSaving(false);
  }
};


  /* Skeleton states */
  if (busy || (!isLoaded && !loadError)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background:"#f5f5f7" }}>
        <div className="spin" />
        <style>{spinnerCSS}</style>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="max-w-xl mx-auto mt-8 p-4 rounded-2xl border" style={{ background:"#fff", borderColor:"#e5e5ea" }}>
        Google Maps nuk u ngarkua. Kontrolloni API key / faturimin.
      </div>
    );
  }

  /* Helpers: open info modal with context-specific text */
  const openInfo = (section) => {
    const blocks = {
      welcome: {
        title: "Si funksionon profili i shpejtë",
        body: (
          <ul className="infoul">
            <li>Plotësoni <strong>Emrin</strong>, <strong>Specialitetin</strong> dhe <strong>Adresën</strong> — këto shfaqen menjëherë te pacientët.</li>
            <li>Shtoni disa <strong>Shërbime</strong> me çmim/kohëzgjatje për pritshmëri të qarta.</li>
            <li>Caktoni <strong>Orarin</strong> për “Disponueshëm sot”.</li>
          </ul>
        )
      },
      name: { title: "Pse kërkohet emri i plotë?", body: <p>Shfaqet si kartvizita juaj në kërkim. Përdorni “Dr.” ose titullin që preferoni.</p> },
      specialty: { title: "Pse kërkohet specialiteti?", body: <p>Ndihmon pacientët t’ju gjejnë në filtrat e MikDok sipas fushës suaj.</p> },
      address: { title: "Adresa & Harta", body: <p>Adresa e saktë gjeneron udhëzime të sakta në Google Maps dhe ul telefonatat për orientim.</p> },
      services: { title: "Shërbimet dhe çmimet", body: <p>Pacientët duan të dinë sa kushton dhe sa zgjat. Mund të përdorni preset ose të shtoni vetë.</p> },
      hours: { title: "Orari tipik", body: <p>Vendos “Hapur sot” dhe intervalet ditore. Fundi duhet të jetë pas nisjes.</p> }
    };
    const b = blocks[section] || blocks.welcome;
    setInfo({ open: true, title: b.title, body: b.body });
  };

  return (
    <div className="min-h-screen">
      <style>{stylesCSS}</style>

      {/* Toasts */}
      <div className="toasts">
        {toasts.map(t => (
          <div key={t.id} className="toast show" role="status" aria-live="polite">
            <CheckCircle2 size={18} />
            <span>{t.message}</span>
          </div>
        ))}
      </div>

      {/* Header */}
      <header className="glass border-b">
        <div className="wrap header">
          <div className="brand" aria-label="Doctor Quick Start">
            <div className="logo" aria-hidden="true"><Activity color="#fff" size={18} /></div>
            <div>
              <div className="title">Doctor Quick Start</div>
              <div className="subtitle">Minimal • I shpejtë</div>
            </div>
          </div>
          <div className="header-actions">
            <div className="trust"><Shield size={14}/> Të dhënat ruhen me kujdes</div>
            <button className="btn ghost" onClick={() => navigate("/doctor-profile-setup")} aria-label="Cilësimet e avancuara">
              <Settings size={16}/> Advanced
            </button>
          </div>
        </div>
      </header>

      <main className="wrap main">
        {/* Welcome */}
        <section className="welcome">
          <div className="welc-left">
            <h1 className="welc-title">Mirë se erdhët në MikDok</h1>
            <button className="btn info" onClick={()=>openInfo("welcome")}><Info size={14}/> Si funksionon</button>
          </div>
          <div className="welc-right">
            <ProgressRing value={completeness} />
          </div>
        </section>

        {/* Alerts */}
        {error && <div className="alert error" role="alert">{error}</div>}
        {ok && <div className="alert ok" role="status">{ok}</div>}

        <section className="card" aria-label="Doctor profile quick form">
          {/* Minimal required */}
          <div className="grid">
            <div>
              <label className="label" htmlFor="fullName">Emri i plotë *</label>
              <div className="rowwithicon">
                <input
                  id="fullName"
                  className="input"
                  placeholder="p.sh. Dr. Arben Dervishi"
                  value={name}
                  onChange={(e)=>setName(e.target.value)}
                  aria-required="true"
                />
                <button className="iconhint" onClick={()=>openInfo("name")} aria-label="Info"><Info size={14}/></button>
              </div>
            </div>
            <div>
              <label className="label" htmlFor="specialty">Specialiteti *</label>
              <div className="rowwithicon">
                <select
                  id="specialty"
                  className="input"
                  value={specialty}
                  onChange={(e)=>setSpecialty(e.target.value)}
                  aria-required="true"
                >
                  <option value="">Zgjidh specialitetin</option>
                  {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <button className="iconhint" onClick={()=>openInfo("specialty")} aria-label="Info"><Info size={14}/></button>
              </div>
            </div>
          </div>

          {/* Address + Map */}
          <div className="block">
            <div className="labelrow">
              <label className="label" htmlFor="address">Adresa e klinikës *</label>
              <button className="iconhint" onClick={()=>openInfo("address")} aria-label="Info"><Info size={14}/></button>
            </div>

            <div className="grid grid-2">
              <Autocomplete
                onLoad={(ac) => (autocompleteRef.current = ac)}
                onPlaceChanged={handlePlaceChanged}
                options={{
                  fields: ["geometry", "formatted_address", "name", "place_id"],
                  componentRestrictions: { country: ["al","it","gr","xk","mk","me"] },
                }}
              >
                <input
                  id="address"
                  ref={addressInputRef}
                  className="input"
                  placeholder="p.sh. Rruga e Durrësit, Tiranë…"
                  value={address}
                  onChange={(e)=>setAddress(e.target.value)}
                  aria-required="true"
                />
              </Autocomplete>

              <button className="btn ghost" onClick={useMyLocation}>
                <Navigation size={16}/> Lokacioni im
              </button>
            </div>

            <div className="mapwrap" role="region" aria-label="Hartë e klinikës">
              <GoogleMap
                onLoad={(map) => (mapRef.current = map)}
                center={mapCenter}
                zoom={zoom}
                mapContainerStyle={{ height: "100%", width: "100%" }}
                options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                onClick={(e) => {
                  if (!e.latLng) return;
                  const c = { lat: e.latLng.lat(), lng: e.latLng.lng() };
                  setCoords(c); setMapCenter(c); setZoom(16); reverseGeocode(c);
                }}
              >
                <Marker
                  position={coords}
                  draggable
                  onDragEnd={(e) => {
                    const c = { lat: e.latLng.lat(), lng: e.latLng.lng() };
                    setCoords(c); setMapCenter(c); reverseGeocode(c);
                  }}
                />
              </GoogleMap>
            </div>

            <div className="hint" aria-live="polite">
              Koordinata: {coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}
            </div>
          </div>

          {/* Services */}
          <div className="block">
            <div className="labelrow">
              <label className="label">Shërbime</label>
              <button className="iconhint" onClick={()=>openInfo("services")} aria-label="Info"><Info size={14}/></button>
            </div>

            <div className="chips">
              {(SERVICE_TEMPLATES[specialty] || []).map((s, i) => (
                <button key={i} type="button" className="chip" onClick={()=>addChipFromTemplate(s)}>
                  + {s.title}
                </button>
              ))}
            </div>

            {/* Currency */}
            <div className="block" style={{ display:"flex", gap:8, alignItems:"center" }}>
              <div className="label" style={{ marginBottom:0 }}>Monedha</div>
              <div className="segmented" role="tablist" aria-label="Currency">
                {CURRENCIES.map(c => (
                  <button
                    key={c}
                    type="button"
                    role="tab"
                    className={`segment ${currency === c ? "active":""}`}
                    onClick={()=>setCurrency(c)}
                    aria-selected={currency === c}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {services.length > 0 && (
              <div className="services">
                {services.map((s, idx)=>(
                  <div key={idx} className="service">
                    <div className="svc-text">
                      <div className="svc-title">{s.title}</div>
                      <div className="svc-meta">
                        {typeof s.price === "number" ? formatMoney(s.price, currency) : "—"} • {s.durationMinutes ? prettyDuration(s.durationMinutes) : "—"}
                      </div>
                    </div>
                    <button className="btn ghost sm" onClick={()=>removeService(idx)} aria-label={`Hiq ${s.title}`}>
                      <X size={14}/>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add custom service */}
            <div className="grid" onKeyDown={(e) => { if (e.key === "Enter") addCustomService(); }}>
              <input
                className="input"
                placeholder="Titulli i shërbimit"
                value={customService.title}
                onChange={(e)=>setCustomService(p=>({ ...p, title: e.target.value }))}
                aria-label="Titulli i shërbimit"
              />
              <input
                className="input"
                placeholder={`Çmimi (${currency === "EUR" ? "€" : "L"} p.sh. 3000)`}
                value={customService.price}
                onChange={(e)=>setCustomService(p=>({ ...p, price: e.target.value }))}
                aria-label="Çmimi"
                inputMode="decimal"
              />
              <input
                className="input"
                placeholder="Kohëzgjatja (30, 30m, 1h 30m)"
                value={customService.duration}
                onChange={(e)=>setCustomService(p=>({ ...p, duration: e.target.value }))}
                onBlur={(e)=>{
                  const v = (e.target.value || "").trim();
                  if (/^\d+$/.test(v)) setCustomService(p=>({ ...p, duration: `${v}m` }));
                }}
                aria-label="Kohëzgjatja"
              />
              <button className="btn ghost" onClick={addCustomService}><Plus size={16}/> Shto</button>
            </div>
          </div>

          {/* Hours */}
          <div className="block">
            <div className="labelrow">
              <label className="label">Orari tipik</label>
              <button className="iconhint" onClick={()=>openInfo("hours")} aria-label="Info"><Info size={14}/></button>
            </div>

            <div className="segmented" role="tablist" aria-label="Presete orari">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  role="tab"
                  aria-selected={activePreset===p.key}
                  className={`segment ${activePreset===p.key ? "active":""}`}
                  onClick={() => applyPreset(p.value, p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="preview-pill">{openToday}</div>

            <div className="hours-grid" aria-label="Orari ditor">
              {DAY_LABELS.map(({ key, label }) => {
                const d = workingHours[key];
                const invalid = d.enabled && (!!d.start && !!d.end) && (() => {
                  const [sh, sm] = d.start.split(":").map(Number);
                  const [eh, em] = d.end.split(":").map(Number);
                  return eh*60+em <= sh*60+sm;
                })();
                return (
                  <div className={`hours-row ${invalid ? "invalid" : ""}`} aria-invalid={invalid || undefined} key={key}>
                    <div className="day">{label}</div>

                    <label className="switch" aria-label={`Aktivizo ${label}`}>
                      <input type="checkbox" checked={d.enabled} onChange={(e)=>setDayEnabled(key, e.target.checked)} />
                      <span className="slider" />
                    </label>

                    {/* START time */}
                    <div className="time-group start">
                      <input
                        type="time"
                        className="time"
                        step="900"
                        value={d.start}
                        disabled={!d.enabled}
                        onChange={(e)=>setDayTime(key,"start",e.target.value)}
                        aria-label={`${label} nisja`}
                      />
                    </div>

                    <span className={`dash ${d.enabled ? "on":"off"}`}>—</span>

                    {/* END time */}
                    <div className="time-group end">
                      <input
                        type="time"
                        className="time"
                        step="900"
                        value={d.end}
                        disabled={!d.enabled}
                        onChange={(e)=>setDayTime(key,"end",e.target.value)}
                        aria-label={`${label} mbarimi`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {invalidDays.length > 0 && (
              <div className="warn">
                ⛔ Kontrolloni: {invalidDays.map(k => DAY_LABELS.find(d => d.key === k)?.label).join(", ")} (përfundimi duhet të jetë pas nisjes).
              </div>
            )}
          </div>

          {/* Desktop actions */}
          <div className="actions desktop-only">
            <button className="btn ghost" onClick={()=>navigate("/doctor-profile-setup")}><Settings size={16}/> Advanced</button>
            <button className="btn primary" onClick={save} disabled={saving || !canSave} aria-disabled={saving || !canSave}>
              {saving ? <span className="spin-sm" /> : <Save size={16}/>}
              {saving ? "Duke ruajtur…" : "Publiko"}
            </button>
          </div>
        </section>
      </main>

      {/* Sticky mobile action bar */}
      <div className="actionbar mobile-only">
        <div className="ab-left">
          <ProgressRing value={completeness} />
          <div className="ab-text">
            <div className="ab-title">Plotësimi</div>
            <div className="ab-sub">{completeness < 100 ? "Vazhdo të plotësosh" : "Gati për publikim"}</div>
          </div>
        </div>
        <div className="ab-actions">
          <button className="btn ghost" onClick={()=>navigate("/doctor-profile-setup")}>Advanced</button>
          <button className="btn primary" onClick={save} disabled={saving || !canSave} aria-disabled={saving || !canSave}>
            {saving ? "Ruaj…" : "Publiko"}
          </button>
        </div>
      </div>

      {/* Info Modal */}
      <Modal
        open={info.open}
        title={info.title}
        onClose={()=>setInfo(s=>({ ...s, open:false }))}
        onContinue={()=>setInfo(s=>({ ...s, open:false }))}
      >
        {info.body}
      </Modal>
    </div>
  );
}

/* -------------------------------
   Styles — mobile-first
----------------------------------*/
const stylesCSS = `
:root{
  --bg:#f6f7fb; --card:#ffffff; --border:#e6e7ef; --text:#15161a; --muted:#6b7280;
  --primary:#0a84ff; --primary-d:#0a76e6;
  --good-bg:#eaf8f0; --good-bd:#b7f3d6; --good-tx:#065f46;
  --warn-bg:#fff7ed; --warn-bd:#fed7aa; --warn-tx:#7c2d12;
  --err-bg:#fef2f2; --err-bd:#fecaca; --err-tx:#7f1d1d;
  --radius:16px; --shadow:0 6px 18px rgba(23,28,38,.06); --shadow-lg:0 14px 34px rgba(23,28,38,.10);
}
*{box-sizing:border-box} html,body,#root{height:100%}
body{margin:0; background:var(--bg); color:var(--text); -webkit-font-smoothing:antialiased; -moz-osx-font-smoothing:grayscale; font-family: ui-sans-serif, system-ui, -apple-system, 'SF Pro Text', Segoe UI, Roboto, Helvetica, Arial;}
.wrap{max-width:980px; margin:0 auto; padding:0 14px}
.header{display:flex; align-items:center; justify-content:space-between; padding:12px 0}
.brand{display:flex; align-items:center; gap:10px}
.logo{display:grid; place-items:center; width:32px; height:32px; border-radius:10px; background:linear-gradient(135deg,#3b82f6,#6366f1); box-shadow:0 4px 12px rgba(59,130,246,.25)}
.title{font-weight:800}
.subtitle{font-size:12px; color:var(--muted)}
.header-actions{display:flex; align-items:center; gap:8px}
.trust{display:none; align-items:center; gap:6px; font-size:12px; color:#3b5bbb; background:#eff5ff; padding:6px 10px; border:1px solid #dbe8ff; border-radius:999px}
.glass{position:sticky; top:0; z-index:10; backdrop-filter:saturate(180%) blur(16px); background:rgba(255,255,255,.72)}
.border-b{border-bottom:1px solid var(--border)}
.main{padding:16px 0 88px}

.welcome{display:flex; align-items:center; justify-content:space-between; margin-bottom:12px}
.welc-title{font-size:18px; margin:0}
.btn.info{padding:8px 10px; font-size:13px}
.ring{position:relative; width:56px; height:56px}
.ring-center{position:absolute; inset:0; display:grid; place-items:center; font-weight:800; font-size:12px}

.card{background:var(--card); border:1px solid var(--border); border-radius:var(--radius); box-shadow:var(--shadow); padding:16px}
.block{margin-top:14px}
.grid{display:grid; gap:10px; grid-template-columns: 1fr}
.grid-2{display:grid; gap:10px; grid-template-columns: 1fr}
@media (min-width: 820px){ .grid{grid-template-columns: repeat(2, minmax(0,1fr))} .grid-2{grid-template-columns: 1.5fr .5fr} .trust{display:inline-flex} }

.label{font-size:12px; color:var(--muted); font-weight:700; margin-bottom:6px}
.labelrow{display:flex; align-items:center; justify-content:space-between}
.rowwithicon{display:flex; gap:8px; align-items:center}
.iconhint{display:inline-flex; align-items:center; justify-content:center; width:38px; height:38px; border-radius:12px; border:1px solid var(--border); background:#fff}
.iconbtn{display:inline-flex; align-items:center; justify-content:center; width:34px; height:34px; border-radius:10px; border:1px solid var(--border); background:#fff}

.input{width:100%; padding:12px 14px; border:1px solid var(--border); border-radius:14px; background:#fafafa; outline:none; font-size:16px}
.input:focus{background:#fff; border-color:var(--primary); box-shadow:0 0 0 3px rgba(10,132,255,.15)}
select.input, .time{appearance:none; background-image:none}

.btn{display:inline-flex; align-items:center; gap:8px; padding:11px 14px; border-radius:999px; border:1px solid var(--border); background:#f7f7f9; color:#111; font-weight:700}
.btn.ghost{background:#fff}
.btn.primary{border-color:transparent; color:#fff; background:linear-gradient(180deg,var(--primary),var(--primary-d))}
.btn.sm{padding:8px 10px}

.actions{display:none; gap:10px; justify-content:flex-end; margin-top:18px}
.desktop-only{display:none}
@media (min-width: 820px){
  .desktop-only{display:flex}
}

.chips{display:flex; gap:8px; flex-wrap:wrap}
.chip{padding:8px 12px; border-radius:999px; border:1px solid var(--border); background:#fff; font-weight:700; font-size:13px}
.services{margin-top:10px; display:flex; flex-direction:column; gap:8px}
.service{display:flex; align-items:center; justify-content:space-between; border:1px solid var(--border); border-radius:14px; padding:10px 12px; background:#fff}
.svc-title{font-weight:700}
.svc-meta{font-size:12px; color:var(--muted); margin-top:2px}

/* Segmented chips (wrap on mobile) */
.segmented{ display:flex; flex-wrap:wrap; gap:8px; border:none; background:transparent }
.segment{
  padding:8px 12px; border:1px solid var(--border);
  border-radius:999px; background:#fff; font-weight:700; font-size:13px
}
.segment.active{ background:var(--primary); color:#fff; border-color:var(--primary) }

.mapwrap{height:260px; width:100%; border-radius:16px; overflow:hidden; border:1px solid var(--border); margin-top:10px}
@media (min-width: 820px){ .mapwrap{height:320px} }

.hint{font-size:12px; color:var(--muted); margin-top:8px}
.alert{border-radius:14px; padding:12px 14px; font-weight:700; margin-bottom:12px}
.alert.ok{background:var(--good-bg); border:1px solid var(--good-bd); color:var(--good-tx)}
.alert.error{background:var(--err-bg); border:1px solid var(--err-bd); color:var(--err-tx)}

/* HOURS GRID — polished, non-truncating */
.hours-grid{
  margin-top:12px; border:1px solid var(--border); border-radius:14px; overflow:hidden;
}

/* Default (fits 360–430px nicely) */
.hours-row{
  display:grid;
  /* day | switch | start | dash | end */
  grid-template-columns: minmax(86px,120px) 52px 1fr 14px 1fr;
  gap:10px; align-items:center;
  padding:10px 6px; border-top:1px solid var(--border); background:var(--card);
}
.hours-row:first-child{border-top:none}
.day{ font-weight:800 }

/* switch */
.switch{ position:relative; display:inline-block; width:46px; height:28px }
.switch input{ opacity:0; width:0; height:0 }
.slider{ position:absolute; inset:0; background:#e9e9ee; border-radius:999px; transition:.2s }
.slider:before{ content:""; position:absolute; width:22px; height:22px; left:3px; top:3px; background:#fff; border-radius:50%; transition:.2s; box-shadow:0 1px 3px rgba(0,0,0,.2) }
.switch input:checked + .slider{ background:#0a84ff }
.switch input:checked + .slider:before{ transform: translateX(18px) }

/* time inputs with icon */
.time-group{ position:relative; min-width:12px }
.time{
  width:100%; padding:10px 10px 10px 15px;
  border:1px solid var(--border); border-radius:12px; background:#fafafa;
  font-size:16px; line-height:1; height:40px;
}
.time-group, .time { min-width: 0; }

.time:hover{ background:#fefefe }
.time:focus{ background:#fff; border-color:#0a84ff; box-shadow:0 0 0 3px rgba(10,132,255,.12) }
.time:disabled{ background:#f3f4f6; color:#9ca3af; opacity:1 }
.time::-webkit-calendar-picker-indicator{ opacity:.6; cursor:pointer }

.dash{ font-weight:700; color:#9aa0a6; text-align:center }

/* Stack to two rows earlier for small phones */
@media (max-width: 430px){
  .hours-row{
    grid-template-columns: 1fr auto 1fr 16px 1fr;
    grid-template-areas:
      "day switch . . ."
      "start start dash end end";
    row-gap:8px;
  }
  .day{ grid-area: day }
  .switch{ grid-area: switch; justify-self:end }
  .time-group.start{ grid-area: start }
  .time-group.end{ grid-area: end }
  .dash{ grid-area: dash; align-self:center; justify-self:center }
}

/* Subtle invalid highlight */
.hours-row.invalid{
  background: linear-gradient(0deg, rgba(255,0,0,.035), rgba(255,0,0,.035)), var(--card);
}

.preview-pill{display:inline-flex; align-items:center; gap:8px; padding:8px 12px; border-radius:999px; margin-top:10px; background:#f2f8ff; color:#0b4a9f; border:1px solid #cfe3ff; font-weight:700; font-size:13px}
.warn{margin-top:10px; font-size:13px; padding:10px 12px; border-radius:12px; background:#fff7ed; border:1px solid #fed7aa; color:#7c2d12}

.toasts{position: fixed; top: 14px; right: 14px; display:flex; flex-direction:column; gap:8px; z-index:50;}
.toast{display:flex; align-items:center; gap:8px; padding:10px 12px; border-radius:12px; background:var(--good-bg); color:var(--good-tx); border:1px solid var(--good-bd); box-shadow:0 10px 24px rgba(0,0,0,.08); opacity:0; transform: translateY(-6px) scale(.98); transition: opacity .25s ease, transform .25s ease; pointer-events:none}
.toast.show{opacity:1; transform:translateY(0) scale(1)}

.actionbar{position:sticky; bottom:0; z-index:20; background:rgba(255,255,255,.95); border-top:1px solid var(--border); backdrop-filter:saturate(160%) blur(10px); padding:10px 12px; display:flex; align-items:center; justify-content:space-between}
.ab-left{display:flex; align-items:center; gap:10px}
.ab-text{font-size:12px}
.ab-title{font-weight:800}
.ab-sub{color:#6b7280}
.ab-actions{display:flex; gap:8px}
.mobile-only{display:flex}
@media (min-width: 820px){ .mobile-only{display:none} }

/* Center the modal on ALL viewports */
.modal-overlay{
  position:fixed; inset:0; z-index:1000;
  background:rgba(0,0,0,.35);
  display:grid; place-items:center;
  padding:16px;
  backdrop-filter:saturate(160%) blur(2px);
}
.modal{
  width:100%; max-width:520px;
  background:#fff;
  border-radius:16px;
  box-shadow:var(--shadow-lg);
  padding:14px 16px 16px;
}
.modal-head{display:flex; align-items:center; justify-content:space-between; gap:8px}
.modal-title{display:flex; align-items:center; gap:8px; font-weight:800}
.modal-body{margin-top:10px; color:#374151}
.modal-actions{display:flex; justify-content:flex-end; gap:8px; margin-top:14px}

.infoul{margin:0; padding-left:18px}
`;

/* Spinner */
const spinnerCSS = `
.spin{width:22px; height:22px; border:2px solid #d1d1d6; border-top-color:#111; border-radius:50%; animation:spin .9s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
`;
